/* Coaches & Trainers: the assignable roster.
 *
 * WHY THIS SCREEN EXISTS. Three systems spell the same coach three different ways -
 * Mindbody sends "Jane Doe", the migrated attendance history says "Coach Jane D.",
 * the CRM column says "Jane Doe" - and a coach's login can be a shared mailbox whose
 * display name matches none of them. Records therefore store a stable staff id, and
 * this is where an owner repairs the mapping.
 *
 * A ROSTER ENTRY WITH NO LOGIN IS NORMAL, not an error: most of the people a gym
 * names on its records never sign in. The one state worth flagging is the opposite -
 * a coach who CAN sign in and whose console would open on nobody.
 *
 * THIS IS NOT THE SUPPORT ROSTER. Support coaches are the front desk: a different
 * roster doing a different job, listed separately and never assignable as an
 * instructor.
 */
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { StaffView } from '../data/contract.js';
import { NoneRow, Stat } from '../components/Panel.js';
import { Avatar } from '../components/Avatar.js';
import { fmt } from '../lib/fmt.js';

export function Trainers() {
  const source = useSource();
  const { data, error } = useAsync(() => source.getStaff(), [source]);

  if (error) return <NoneRow>Could not read the roster. {error.message}</NoneRow>;
  if (!data) return <NoneRow>Reading the roster…</NoneRow>;

  const instructors = data.filter((s) => s.kind === 'instructor')
    .sort((a, b) => b.visits - a.visits || a.name.localeCompare(b.name));
  const support = data.filter((s) => s.kind === 'support');
  const desk = data.filter((s) => s.kind === 'desk' || s.kind === 'admin');
  const linked = instructors.filter((s) => s.linked).length;

  return (
    <>
      <section className="panel stats">
        <Stat value={instructors.length} label="Trainers" />
        <Stat value={linked} label="Linked to a login" />
        <Stat value={support.length} label="Support coaches" />
        <Stat value={desk.length} label="Desk and admin" />
      </section>

      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Trainers</h2>
          <span className="eyebrow t-label-xsmall">Who teaches here</span>
        </header>
        <Roster rows={instructors} empty="No trainers on the roster yet. They appear once class attendance is synced." />
      </section>

      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Support coaches and the desk</h2>
          <span className="eyebrow t-label-xsmall">A different roster, a different job</span>
        </header>
        <Roster rows={[...support, ...desk]} empty="Nobody on the support roster yet." teaching={false} />
      </section>
    </>
  );
}

function Roster({ rows, empty, teaching = true }: { rows: StaffView[]; empty: string; teaching?: boolean }) {
  if (!rows.length) return <NoneRow>{empty}</NoneRow>;
  return (
    <table className="table">
      <thead>
        <tr className="t-label-small">
          <th>Name</th><th>Login</th>
          {teaching && <th className="right">Visits coached</th>}
          {teaching && <th>Last taught</th>}
          <th className="right">Members</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id} style={{ cursor: 'default' }}>
            <td>
              <Avatar name={s.name} photoUrl={s.photoUrl} size="sm" />
              <span className="t-label-large">{s.name}</span>
              {!s.active && <span className="badge t-label-xsmall">inactive</span>}
            </td>
            <td className="t-body-small muted">
              {s.linked ? s.linked.email : <span className="muted">not linked</span>}
            </td>
            {teaching && <td className="right t-body-small">{s.visits ? fmt.num(s.visits) : <span className="muted">none yet</span>}</td>}
            {teaching && <td className="t-body-small muted">{s.lastTaught ? fmt.date(s.lastTaught) : 'not on the timetable'}</td>}
            <td className="right t-body-small">{s.members || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
